import {
  HHAlert,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { createStyles, makeStyles } from '@mui/styles';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useMemo } from 'react';
import {
  useGetPaymentTypesQuery,
  useGetResolvedProcedureCodesQuery,
} from '../../types';
import LoadingComponent from '../utils/loading';

export const RESOLVED_CPT_CODES_TEST_ID = 'resolved-cpt-codes-section';

const useStyles = makeStyles(theme =>
  createStyles({
    oddRow: {
      backgroundColor: theme.palette.background.default,
    },
    evenRow: {
      backgroundColor: theme.palette.background.paper,
    },
  }),
);

interface ResolvedProcedureCodesGridProps {
  clientId: number;
  insurerId: number;
  partnershipId?: number;
}

const columns: GridColDef[] = [
  {
    field: 'paymentTypeCode',
    headerName: 'Payment Type',
    flex: 1,
  },
  {
    field: 'cptCode',
    headerName: 'CPT Code',
    flex: 1,
  },
  {
    field: 'modifier1',
    headerName: 'Modifier 1',
    flex: 0.7,
  },
  {
    field: 'modifier2',
    headerName: 'Modifier 2',
    flex: 0.7,
  },
];

export const ResolvedProcedureCodesGrid = ({
  clientId,
  insurerId,
  partnershipId,
}: ResolvedProcedureCodesGridProps): JSX.Element | null => {
  const classes = useStyles();
  // ACCOUNTS-AND-PLAN-MIGRATION: clientId, insurerId, partnershipId are client/insurer/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const { data, loading, error } = useGetResolvedProcedureCodesQuery({
    variables: { clientId, insurerId, partnershipId },
  });

  const { data: paymentTypesData, loading: paymentTypesLoading } =
    useGetPaymentTypesQuery();

  const paymentTypeMap = useMemo(() => {
    const map = new Map<number, string>();
    paymentTypesData?.getPaymentTypes?.forEach(pt => map.set(pt.id, pt.code));
    return map;
  }, [paymentTypesData]);

  const rows: GridRowsProp = useMemo(
    () =>
      (data?.getResolvedProcedureCodes ?? []).map(item => ({
        id: item.id,
        cptCode: item.cptCode,
        modifier1: item.modifier1 ?? '--',
        modifier2: item.modifier2 ?? '--',
        paymentTypeCode:
          paymentTypeMap.get(item.paymentTypeId) ??
          `Unknown (${item.paymentTypeId})`,
      })),
    [data, paymentTypeMap],
  );

  if (loading || paymentTypesLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return (
      <HHAlert hhVariant="outlined" severity="error">
        <HHTypography hhVariant="body">
          There was an error loading resolved CPT codes
        </HHTypography>
      </HHAlert>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <HHStack
      direction="column"
      spacing={2}
      data-testid={RESOLVED_CPT_CODES_TEST_ID}
    >
      <HHTypography hhVariant="h2">Resolved CPT Codes</HHTypography>
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        hideFooter
        disableRowSelectionOnClick
        disableVirtualization
        initialState={{
          sorting: { sortModel: [{ field: 'paymentTypeCode', sort: 'asc' }] },
        }}
        getRowClassName={(params): string =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? classes.evenRow
            : classes.oddRow
        }
        sx={{
          border: 0,
          '& .MuiDataGrid-iconSeparator': {
            display: 'none',
          },
        }}
      />
    </HHStack>
  );
};
